(() => {
  const config = window.WobWishlistConfig || {};
  const headerCountStorageKey = 'wob:wishlist-count:v1';
  const activeListStorageKey = 'wob:wishlist-active-list:v1';
  const productCache = new Map();
  const productSocialCountCache = new Map();
  let wishlistListsCache = null;
  let wishlistListFetchPromise = null;
  let swymReady = false;
  let activeSwat = null;
  let swymEventsBound = false;
  let refreshTimer = null;
  let productSocialCountTimer = null;
  let wishlistListModalState = null;

  const getStringId = (value) => {
    return String(value || '')
      .replace('gid://shopify/Product/', '')
      .replace('gid://shopify/ProductVariant/', '');
  };

  const getNumericOrStringId = (value) => {
    const stringValue = getStringId(value);
    const numericValue = Number.parseInt(stringValue, 10);
    return Number.isFinite(numericValue) ? numericValue : stringValue;
  };

  const getCustomerKey = () => {
    return String(config.customerKey || window.swymCustomerId || 'guest');
  };

  const readStoredHeaderCount = () => {
    try {
      const stored = JSON.parse(window.localStorage.getItem(headerCountStorageKey) || 'null');
      const count = Number(stored && stored.customerKey === getCustomerKey() ? stored.count : NaN);

      return Number.isFinite(count) && count >= 0 ? count : null;
    } catch (error) {
      return null;
    }
  };

  const writeStoredHeaderCount = (count) => {
    try {
      window.localStorage.setItem(headerCountStorageKey, JSON.stringify({
        count,
        customerKey: getCustomerKey(),
        updatedAt: Date.now()
      }));
    } catch (error) {
      // Storage is only used to avoid header count flicker between navigations.
    }
  };

  const getCurrency = () => {
    return config.currency || window.Shopify?.currency?.active || 'USD';
  };

  const formatMoney = (cents) => {
    const numericCents = Number(cents);
    if (!Number.isFinite(numericCents)) return '';

    try {
      return new Intl.NumberFormat(config.locale || document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: getCurrency()
      }).format(numericCents / 100);
    } catch (error) {
      return `$${(numericCents / 100).toFixed(2)}`;
    }
  };

  const getFallbackCents = (value) => {
    const price = Number(value);
    if (!Number.isFinite(price)) return null;
    return Math.round(price < 1000 ? price * 100 : price);
  };

  const getImageSrc = (image) => {
    if (!image) return '';
    if (typeof image === 'string') return image;
    return image.src || image.preview_image?.src || '';
  };

  const getWishlistItemKey = (item) => {
    return `${getStringId(item?.empi)}:${getStringId(item?.epi)}`;
  };

  const normalizeWishlistItems = (items, list = {}) => {
    const normalized = [];
    const seen = new Set();

    (Array.isArray(items) ? items : []).forEach((item) => {
      if (!item) return;

      const product = item.product || item;
      const epi = product.epi || item.epi;
      const empi = product.empi || item.empi;
      const du = product.du || product.uri || item.du || item.uri;
      const key = getWishlistItemKey({ empi, epi });

      if (!epi || !empi || !du || seen.has(key)) return;

      seen.add(key);
      normalized.push({
        ...item,
        ...product,
        epi,
        empi,
        du,
        lid: item.lid || product.lid || list.lid || '',
        lname: item.lname || product.lname || list.lname || ''
      });
    });

    return normalized;
  };

  const normalizeWishlistLists = (lists) => {
    return (Array.isArray(lists) ? lists : [])
      .filter((list) => list && list.st !== 0)
      .map((list, index) => {
        const lid = list.lid || list.listid || '';
        const lname = String(list.lname || list.name || (index === 0 ? 'My Wishlist' : 'Wishlist')).trim() || 'Wishlist';

        return {
          ...list,
          lid,
          lname,
          listcontents: normalizeWishlistItems(list.listcontents || list.contents || [], { lid, lname })
        };
      })
      .filter((list) => list.lid || list.listcontents.length);
  };

  const invalidateWishlistLists = () => {
    wishlistListsCache = null;
    wishlistListFetchPromise = null;
  };

  const fetchLists = (swat) => {
    if (typeof swat.fetchLists !== 'function') return Promise.resolve([]);

    return new Promise((resolve, reject) => {
      swat.fetchLists({
        callbackFn: (lists) => resolve(Array.isArray(lists) ? lists : []),
        errorFn: reject
      });
    });
  };

  const fetchWishlistItemsFallback = (swat) => {
    if (typeof swat.fetch === 'function') {
      return new Promise((resolve) => {
        let settled = false;
        const timeout = window.setTimeout(() => {
          if (settled) return;
          settled = true;
          resolve([]);
        }, 10000);

        const finish = (items) => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timeout);
          resolve(normalizeWishlistItems(items));
        };

        try {
          swat.fetch(finish);
        } catch (error) {
          finish([]);
        }
      });
    }

    return Promise.resolve([]);
  };

  const fetchWishlistLists = async (swat, { force = false } = {}) => {
    if (!force && wishlistListsCache) return wishlistListsCache;
    if (!force && wishlistListFetchPromise) return wishlistListFetchPromise;

    wishlistListFetchPromise = (async () => {
      let lists = [];

      try {
        lists = normalizeWishlistLists(await fetchLists(swat));
      } catch (error) {
        lists = [];
      }

      if (!lists.length) {
        const fallbackItems = await fetchWishlistItemsFallback(swat);

        if (fallbackItems.length) {
          lists = [{
            lid: '',
            lname: 'My Wishlist',
            listcontents: fallbackItems
          }];
        }
      }

      wishlistListsCache = lists;
      return lists;
    })();

    try {
      return await wishlistListFetchPromise;
    } finally {
      wishlistListFetchPromise = null;
    }
  };

  const getWishlistItemsFromLists = (lists, listId = 'all') => {
    const normalizedListId = String(listId || 'all');
    const normalizedLists = normalizeWishlistLists(lists);

    if (normalizedListId !== 'all') {
      const list = normalizedLists.find((candidate) => String(candidate.lid) === normalizedListId);

      return (list?.listcontents || []).map((item) => ({
        ...item,
        lid: item.lid || list.lid,
        lname: item.lname || list.lname,
        listMemberships: [{ lid: list.lid, lname: list.lname }]
      }));
    }

    const groupedItems = new Map();

    normalizedLists.forEach((list) => {
      list.listcontents.forEach((item) => {
        const key = getWishlistItemKey(item);
        const current = groupedItems.get(key) || {
          ...item,
          lid: item.lid || list.lid,
          lname: item.lname || list.lname,
          listMemberships: []
        };

        if (list.lid && !current.listMemberships.some((membership) => membership.lid === list.lid)) {
          current.listMemberships.push({ lid: list.lid, lname: list.lname });
        }

        groupedItems.set(key, current);
      });
    });

    return Array.from(groupedItems.values());
  };

  const fetchWishlistItems = async (swat, { force = false, lists = null, listId = 'all' } = {}) => {
    const nextLists = lists || await fetchWishlistLists(swat, { force });

    return getWishlistItemsFromLists(nextLists, listId);
  };

  const readStoredActiveListId = () => {
    try {
      const stored = JSON.parse(window.localStorage.getItem(activeListStorageKey) || 'null');

      return stored && stored.customerKey === getCustomerKey() ? String(stored.listId || 'all') : 'all';
    } catch (error) {
      return 'all';
    }
  };

  const writeStoredActiveListId = (listId) => {
    try {
      window.localStorage.setItem(activeListStorageKey, JSON.stringify({
        customerKey: getCustomerKey(),
        listId: listId || 'all',
        updatedAt: Date.now()
      }));
    } catch (error) {
      // The active list preference is a convenience only.
    }
  };

  const getActiveWishlistListId = (lists, root = null) => {
    const listIds = new Set(normalizeWishlistLists(lists).map((list) => String(list.lid)).filter(Boolean));
    const preferredListId = root?.dataset.activeWishlistList || readStoredActiveListId();

    if (preferredListId === 'all' || listIds.has(preferredListId)) return preferredListId;

    return 'all';
  };

  const setActiveWishlistListId = (listId, root = null) => {
    const nextListId = String(listId || 'all');

    if (root) root.dataset.activeWishlistList = nextListId;
    writeStoredActiveListId(nextListId);
  };

  const updateHeaderCount = (count, { persist = true } = {}) => {
    if (persist) writeStoredHeaderCount(count);

    document.querySelectorAll('[data-wishlist-count]').forEach((element) => {
      element.textContent = String(count);
      element.hidden = count === 0;
    });
  };

  const findWishlistedItem = (items, product) => {
    const productId = getStringId(product.empi);
    const variantId = getStringId(product.epi);

    return items.find((item) => {
      return getStringId(item.empi) === productId && getStringId(item.epi) === variantId;
    });
  };

  const getButtonProduct = (button) => {
    const priceCents = Number(button.dataset.wishlistPrice || 0);

    return {
      epi: getNumericOrStringId(button.dataset.wishlistVariantId),
      empi: getNumericOrStringId(button.dataset.wishlistProductId),
      du: button.dataset.wishlistProductUrl || window.location.href.split('?')[0],
      dt: button.dataset.wishlistTitle || document.querySelector('h1')?.textContent?.trim() || '',
      iu: button.dataset.wishlistImage || '',
      pr: Number.isFinite(priceCents) ? priceCents / 100 : undefined,
      stk: button.dataset.wishlistAvailable === 'false' ? 0 : 1,
      source: 'pdp'
    };
  };

  const isWishlistVariantReady = (button) => {
    return Boolean(button) && button.dataset.wishlistVariantSelected !== 'false';
  };

  const setWishlistVariantPendingState = (button, isPending) => {
    if (!button) return;

    button.dataset.wishlistVariantSelected = isPending ? 'false' : 'true';

    if (isPending) {
      button.disabled = true;
      button.classList.remove('product-wishlist-button--active', 'swym-added');
      button.setAttribute('aria-pressed', 'false');
      button.setAttribute('aria-label', 'Select size to save to wishlist');
      button.dataset.wishlistListId = '';
      button.dataset.wishlistListIds = '';
      return;
    }

    if (!button.classList.contains('is-loading')) {
      button.disabled = false;
    }

    button.setAttribute(
      'aria-label',
      button.getAttribute('aria-pressed') === 'true' ? 'Manage wishlist lists' : 'Add to wishlist'
    );
  };

  const setProductButtonState = (button, wishlistedItem) => {
    if (!isWishlistVariantReady(button)) {
      setWishlistVariantPendingState(button, true);
      return;
    }

    const isWishlisted = Boolean(wishlistedItem);
    const listMemberships = wishlistedItem?.listMemberships || (wishlistedItem?.lid ? [{ lid: wishlistedItem.lid, lname: wishlistedItem.lname || '' }] : []);

    button.classList.toggle('product-wishlist-button--active', isWishlisted);
    button.classList.toggle('swym-added', isWishlisted);
    button.setAttribute('aria-pressed', isWishlisted ? 'true' : 'false');
    button.setAttribute('aria-label', isWishlisted ? 'Manage wishlist lists' : 'Add to wishlist');
    button.dataset.wishlistListId = listMemberships[0]?.lid || wishlistedItem?.lid || '';
    button.dataset.wishlistListIds = listMemberships.map((membership) => membership.lid).filter(Boolean).join(',');

    if (!button.classList.contains('is-loading')) {
      button.disabled = false;
    }
  };

  const updateProductButtons = (items) => {
    document.querySelectorAll('[data-wishlist-button]').forEach((button) => {
      setProductButtonState(button, findWishlistedItem(items, getButtonProduct(button)));
    });
  };

  const formatProductSocialCount = (count) => {
    const numericCount = Number(count);

    if (!Number.isFinite(numericCount)) return '0';
    if (numericCount < 1000) return String(numericCount);

    try {
      return new Intl.NumberFormat(config.locale || document.documentElement.lang || 'en-US', {
        maximumFractionDigits: 1,
        notation: 'compact'
      }).format(numericCount);
    } catch (error) {
      return String(numericCount);
    }
  };

  const normalizeProductSocialCount = (response, productId) => {
    const requestedProductId = getStringId(productId);
    const data = response?.data ?? response;
    const entries = Array.isArray(data) ? data : [data];
    const match = entries.find((entry) => {
      const entryProductId = getStringId(entry?.empi || entry?.productId || entry?.id);

      return requestedProductId ? entryProductId === requestedProductId : true;
    }) || entries[0] || null;
    const count = Number(match?.count ?? match?.data?.count ?? response?.count ?? 0);

    return Number.isFinite(count) && count >= 0 ? count : 0;
  };

  const fetchProductSocialCount = (swat, productId) => {
    const empi = getNumericOrStringId(productId);

    if (!empi || typeof swat?.wishlist?.getSocialCount !== 'function') {
      return Promise.resolve(null);
    }

    return new Promise((resolve) => {
      let settled = false;
      const timeout = window.setTimeout(() => {
        if (settled) return;
        settled = true;
        resolve(null);
      }, 8000);

      const finish = (response) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        resolve(normalizeProductSocialCount(response, productId));
      };

      const fail = () => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        resolve(null);
      };

      try {
        swat.wishlist.getSocialCount({ empi }, finish, fail);
      } catch (error) {
        fail();
      }
    });
  };

  const updateProductSocialCount = (productId, count) => {
    const requestedProductId = getStringId(productId);
    const numericCount = Number(count);
    const hasCount = Number.isFinite(numericCount) && numericCount >= 0;
    const label = `${numericCount} ${numericCount === 1 ? 'wishlist save' : 'wishlist saves'}`;

    document.querySelectorAll('[data-product-wishlist-social-count][data-product-id]').forEach((element) => {
      if (getStringId(element.dataset.productId) !== requestedProductId) return;

      const value = element.querySelector('[data-product-wishlist-social-count-value]');

      if (value) value.textContent = formatProductSocialCount(numericCount);
      element.hidden = !hasCount;
      element.title = hasCount ? label : '';
    });
  };

  const refreshProductSocialCounts = async ({ force = false } = {}) => {
    if (!activeSwat) return;

    const productIds = Array.from(new Set(
      Array.from(document.querySelectorAll('[data-product-wishlist-social-count][data-product-id]'))
        .map((element) => getStringId(element.dataset.productId))
        .filter(Boolean)
    ));

    if (!productIds.length) return;

    await Promise.all(productIds.map(async (productId) => {
      if (!force && productSocialCountCache.has(productId)) {
        updateProductSocialCount(productId, productSocialCountCache.get(productId));
        return;
      }

      const count = await fetchProductSocialCount(activeSwat, productId);

      if (count === null) return;

      productSocialCountCache.set(productId, count);
      updateProductSocialCount(productId, count);
    }));
  };

  const scheduleProductSocialCountRefresh = ({ force = false } = {}) => {
    window.clearTimeout(productSocialCountTimer);
    productSocialCountTimer = window.setTimeout(() => {
      refreshProductSocialCounts({ force });
    }, 120);
  };

  const setButtonBusy = (button, isBusy) => {
    button.classList.toggle('is-loading', isBusy);
    button.setAttribute('aria-busy', isBusy ? 'true' : 'false');
    button.disabled = isBusy || !isWishlistVariantReady(button);

    if (!isBusy && !isWishlistVariantReady(button)) {
      setWishlistVariantPendingState(button, true);
    }
  };

  const getListApiProduct = (product) => {
    const listProduct = {
      epi: getNumericOrStringId(product?.epi),
      empi: getNumericOrStringId(product?.empi),
      du: product?.du || ''
    };

    if (product?.dt) listProduct.dt = product.dt;
    if (product?.iu) listProduct.iu = product.iu;
    if (product?.pr !== undefined) listProduct.pr = product.pr;
    if (product?.source) listProduct.source = product.source;
    if (product?.qty !== undefined) listProduct.qty = product.qty;
    if (product?.note) listProduct.note = product.note;
    if (product?.cprops) listProduct.cprops = product.cprops;

    return listProduct;
  };

  const createWishlistList = (swat, listName) => {
    return new Promise((resolve, reject) => {
      const name = String(listName || '').trim();

      if (!name) {
        reject(new Error('Enter a list name.'));
        return;
      }

      if (typeof swat.createList !== 'function') {
        reject(new Error('Wishlist list creation is unavailable.'));
        return;
      }

      swat.createList({ lname: name }, (list) => {
        invalidateWishlistLists();
        resolve(list);
      }, reject);
    });
  };

  const getDefaultWishlistList = async (swat) => {
    const lists = await fetchWishlistLists(swat);

    if (lists[0]?.lid) return lists[0];

    const list = await createWishlistList(swat, 'My Wishlist');

    return {
      ...list,
      listcontents: []
    };
  };

  const addWishlistItem = (swat, product, listId = '') => {
    return new Promise((resolve, reject) => {
      const listProduct = getListApiProduct(product);

      if (listId && typeof swat.addToList === 'function') {
        swat.addToList(listId, listProduct, (item) => {
          invalidateWishlistLists();
          resolve(item);
        }, reject);
        return;
      }

      if (!listId && typeof swat.addToList === 'function') {
        getDefaultWishlistList(swat)
          .then((list) => {
            if (!list?.lid) throw new Error('Wishlist list API is unavailable.');
            swat.addToList(list.lid, listProduct, (item) => {
              invalidateWishlistLists();
              resolve(item);
            }, reject);
          })
          .catch(reject);
        return;
      }

      if (typeof swat.addToWishList === 'function') {
        swat.addToWishList(product, (item) => {
          invalidateWishlistLists();
          resolve(item);
        }, reject);
        return;
      }

      reject(new Error('Wishlist add API is unavailable.'));
    });
  };

  const removeWishlistItem = (swat, product, listId = '') => {
    return new Promise((resolve, reject) => {
      const listProduct = getListApiProduct(product);

      if (listId && typeof swat.deleteFromList === 'function') {
        swat.deleteFromList(listId, listProduct, (item) => {
          invalidateWishlistLists();
          resolve(item);
        }, reject);
        return;
      }

      if (!listId && typeof swat.deleteFromList === 'function') {
        fetchWishlistLists(swat, { force: true })
          .then((lists) => {
            const matches = normalizeWishlistLists(lists)
              .flatMap((list) => list.listcontents.map((item) => ({ ...item, lid: list.lid })))
              .filter((item) => findWishlistedItem([item], product));

            if (!matches.length) throw new Error('Wishlist item could not be found.');

            return Promise.all(matches.map((match) => {
              return new Promise((deleteResolve, deleteReject) => {
                swat.deleteFromList(match.lid, listProduct, deleteResolve, deleteReject);
              });
            }));
          })
          .then((items) => {
            invalidateWishlistLists();
            resolve(items);
          })
          .catch(reject);
        return;
      }

      if (typeof swat.removeFromWishList === 'function') {
        swat.removeFromWishList(product, (item) => {
          invalidateWishlistLists();
          resolve(item);
        }, reject);
        return;
      }

      reject(new Error('Wishlist remove API is unavailable.'));
    });
  };

  const hasProductButtons = () => {
    return Boolean(document.querySelector('[data-wishlist-button]'));
  };

  const supportsWishlistLists = (swat) => {
    return Boolean(
      swat
        && typeof swat.fetchLists === 'function'
        && typeof swat.addToList === 'function'
        && typeof swat.deleteFromList === 'function'
    );
  };

  const shouldFetchWishlistItems = ({ renderPage = false, force = false } = {}) => {
    if (force || renderPage || hasProductButtons()) return true;
    return readStoredHeaderCount() === null;
  };

  const refreshSurfaces = async ({ renderPage = false, force = false } = {}) => {
    if (!activeSwat) return;

    if (!shouldFetchWishlistItems({ renderPage, force })) return;

    const lists = await fetchWishlistLists(activeSwat, { force });
    const items = getWishlistItemsFromLists(lists, 'all');

    updateHeaderCount(items.length);
    updateProductButtons(items);

    if (renderPage) {
      await renderWishlistPage(activeSwat, { lists });
    }
  };

  const scheduleRefresh = ({ renderPage = false, force = false } = {}) => {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(() => {
      refreshSurfaces({ renderPage, force });
    }, 120);
  };

  const updateProductFromVariant = (button, variant, product) => {
    if (!button || !variant) return;

    button.dataset.wishlistVariantId = String(variant.id || button.dataset.wishlistVariantId || '');
    button.dataset.wishlistPrice = String(variant.price || button.dataset.wishlistPrice || '');
    button.dataset.wishlistAvailable = variant.available ? 'true' : 'false';

    const variantImage = getImageSrc(variant.featured_image);
    const productImage = getImageSrc(product?.featured_image);
    const nextImage = variantImage || productImage;

    if (nextImage) button.dataset.wishlistImage = nextImage;
    if (product?.title) button.dataset.wishlistTitle = product.title;
    if (product?.url) button.dataset.wishlistProductUrl = `${window.location.origin}${product.url}`;

    setWishlistVariantPendingState(button, false);
  };

  const initProductButtons = (swat) => {
    document.querySelectorAll('[data-wishlist-button]').forEach((button) => {
      if (button.dataset.wishlistInitialized === 'true') return;

      button.dataset.wishlistInitialized = 'true';
      if (isWishlistVariantReady(button)) {
        button.disabled = false;
      } else {
        setWishlistVariantPendingState(button, true);
      }

      button.addEventListener('click', async () => {
        if (!isWishlistVariantReady(button)) {
          setWishlistVariantPendingState(button, true);
          return;
        }

        const product = getButtonProduct(button);
        const isActive = button.getAttribute('aria-pressed') === 'true';

        setButtonBusy(button, true);

        try {
          if (supportsWishlistLists(swat)) {
            await openWishlistListModal({ mode: 'product', product, button });
            return;
          }

          if (isActive) {
            await removeWishlistItem(swat, product, button.dataset.wishlistListId);
          } else {
            await addWishlistItem(swat, product);
          }

          await refreshSurfaces({ renderPage: Boolean(document.querySelector('[data-wishlist-page]')) });
          scheduleProductSocialCountRefresh({ force: true });
        } catch (error) {
          button.setAttribute('aria-label', 'Wishlist is unavailable');
        } finally {
          setButtonBusy(button, false);
        }
      });
    });
  };

  const initVariantListener = () => {
    if (document.documentElement.dataset.wishlistVariantListener === 'true') return;

    document.documentElement.dataset.wishlistVariantListener = 'true';
    document.addEventListener('variant:change', (event) => {
      const variant = event.detail?.variant;
      const product = event.detail?.product;
      const button = document.querySelector('[data-wishlist-button]');

      updateProductFromVariant(button, variant, product);
      scheduleRefresh();
    });

    document.addEventListener('variant:pending', () => {
      const button = document.querySelector('[data-wishlist-button]');

      setWishlistVariantPendingState(button, true);
    });
  };

  const getProductJsonPath = (url) => {
    if (!url) return '';

    try {
      const parsed = new URL(url, window.location.origin);
      return `${parsed.pathname.replace(/\/$/, '')}.js`;
    } catch (error) {
      return '';
    }
  };

  const fetchProductJson = async (item) => {
    const path = getProductJsonPath(item.du);
    if (!path) return null;
    if (productCache.has(path)) return productCache.get(path);

    const request = fetch(path)
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);

    productCache.set(path, request);
    return request;
  };

  const hydrateWishlistItem = async (item) => {
    const product = await fetchProductJson(item);
    const variant = product?.variants?.find((variantItem) => getStringId(variantItem.id) === getStringId(item.epi));
    const activeVariant = variant || product?.variants?.find((variantItem) => variantItem.available) || product?.variants?.[0];
    const productPath = product?.url || getProductJsonPath(item.du).replace(/\.js$/, '') || item.du || '#';
    const productUrl = activeVariant?.id ? `${productPath}?variant=${activeVariant.id}` : productPath;
    const image = getImageSrc(activeVariant?.featured_image) || getImageSrc(product?.featured_image) || item.iu || '';
    const priceCents = Number.isFinite(Number(activeVariant?.price))
      ? Number(activeVariant.price)
      : getFallbackCents(item.pr);
    const compareAtCents = Number.isFinite(Number(activeVariant?.compare_at_price))
      ? Number(activeVariant.compare_at_price)
      : null;

    return {
      item,
      productId: getStringId(item.empi),
      variantId: getStringId(activeVariant?.id || item.epi),
      title: product?.title || item.dt || 'Product',
      variantTitle: activeVariant?.title && activeVariant.title !== 'Default Title' ? activeVariant.title : '',
      url: productUrl,
      image,
      priceCents,
      compareAtCents,
      listMemberships: item.listMemberships || (item.lid ? [{ lid: item.lid, lname: item.lname || '' }] : []),
      available: activeVariant?.available ?? item.stk !== 0,
      productAvailable: Boolean(product),
      removeProduct: {
        epi: getNumericOrStringId(item.epi),
        empi: getNumericOrStringId(item.empi),
        du: item.du
      }
    };
  };

  const createElement = (tagName, className = '', text = '') => {
    const element = document.createElement(tagName);
    if (className) element.className = className;
    if (text) element.textContent = text;
    return element;
  };

  const getProductMembershipIds = (lists, product) => {
    const membershipIds = new Set();

    normalizeWishlistLists(lists).forEach((list) => {
      if (!list.lid) return;
      if (list.listcontents.some((item) => findWishlistedItem([item], product))) {
        membershipIds.add(list.lid);
      }
    });

    return membershipIds;
  };

  const getWishlistListModalElements = () => {
    const modal = document.querySelector('[data-wishlist-list-modal]');
    if (!modal) return null;

    return {
      modal,
      panel: modal.querySelector('.wishlist-list-modal__panel'),
      title: modal.querySelector('[data-wishlist-list-modal-title]'),
      product: modal.querySelector('[data-wishlist-list-modal-product]'),
      options: modal.querySelector('[data-wishlist-list-options]'),
      feedback: modal.querySelector('[data-wishlist-list-feedback]'),
      footer: modal.querySelector('[data-wishlist-list-modal-footer]'),
      saveButton: modal.querySelector('[data-wishlist-list-save]'),
      createForm: modal.querySelector('[data-wishlist-create-list-form]'),
      createInput: modal.querySelector('[data-wishlist-list-name]'),
      createSubmit: modal.querySelector('[data-wishlist-list-create-submit]')
    };
  };

  const setWishlistListModalFeedback = (message = '', isError = false) => {
    const elements = getWishlistListModalElements();
    if (!elements?.feedback) return;

    elements.feedback.hidden = !message;
    elements.feedback.textContent = message;
    elements.feedback.classList.toggle('wishlist-list-modal__feedback--error', isError);
  };

  const setWishlistListModalBusy = (isBusy) => {
    const elements = getWishlistListModalElements();
    const buttons = [
      elements?.saveButton,
      elements?.createSubmit,
      ...Array.from(elements?.modal?.querySelectorAll('[data-wishlist-list-modal-close]') || [])
    ].filter(Boolean);

    buttons.forEach((button) => {
      button.disabled = isBusy;
      button.classList.toggle('is-loading', isBusy);
      button.setAttribute('aria-busy', isBusy ? 'true' : 'false');
    });
  };

  const renderWishlistListModalOptions = (lists) => {
    const elements = getWishlistListModalElements();
    const state = wishlistListModalState;

    if (!elements || !state) return;

    const normalizedLists = normalizeWishlistLists(lists);
    const isProductMode = state.mode === 'product';

    elements.title.textContent = isProductMode ? 'Save to wishlist' : 'Create wishlist';
    elements.footer.hidden = !isProductMode;
    elements.options.hidden = !isProductMode;
    elements.product.hidden = !isProductMode || !state.product?.dt;
    elements.product.textContent = state.product?.dt || '';
    elements.createSubmit.textContent = isProductMode ? 'Create and save' : 'Create list';
    elements.options.replaceChildren();
    setWishlistListModalFeedback();

    if (!isProductMode) return;

    if (!normalizedLists.length) {
      const empty = createElement('p', 'wishlist-list-modal__empty', 'Create a list to save this item.');
      elements.options.append(empty);
      return;
    }

    normalizedLists.forEach((list, index) => {
      const option = createElement('label', 'wishlist-list-modal__option');
      const checkbox = document.createElement('input');
      const shouldAutoCheck = state.initialListIds.size === 0 && normalizedLists.length === 1 && index === 0;
      const name = createElement('span', 'wishlist-list-modal__option-name', list.lname);
      const count = createElement(
        'span',
        'wishlist-list-modal__option-count',
        `${list.listcontents.length} ${list.listcontents.length === 1 ? 'item' : 'items'}`
      );

      checkbox.type = 'checkbox';
      checkbox.value = list.lid;
      checkbox.checked = state.initialListIds.has(list.lid) || shouldAutoCheck;
      checkbox.dataset.wishlistListChoice = '';

      option.append(checkbox, name, count);
      elements.options.append(option);
    });
  };

  const closeWishlistListModal = () => {
    const elements = getWishlistListModalElements();
    const previousFocus = wishlistListModalState?.previousFocus;

    if (!elements?.modal) return;

    elements.modal.classList.remove('wishlist-list-modal--open');
    elements.modal.hidden = true;
    document.documentElement.classList.remove('wishlist-list-modal-is-open');
    document.body.classList.remove('wishlist-list-modal-is-open');
    wishlistListModalState = null;

    if (previousFocus instanceof HTMLElement) {
      previousFocus.focus({ preventScroll: true });
    }
  };

  const openWishlistListModal = async ({ mode = 'create', product = null, button = null } = {}) => {
    const elements = getWishlistListModalElements();
    if (!elements?.modal || !activeSwat) return;

    const lists = await fetchWishlistLists(activeSwat);
    const initialListIds = product ? getProductMembershipIds(lists, product) : new Set();

    wishlistListModalState = {
      mode,
      product,
      button,
      initialListIds,
      previousFocus: document.activeElement instanceof HTMLElement ? document.activeElement : null
    };

    if (elements.createInput) elements.createInput.value = '';
    renderWishlistListModalOptions(lists);

    elements.modal.hidden = false;
    document.documentElement.classList.add('wishlist-list-modal-is-open');
    document.body.classList.add('wishlist-list-modal-is-open');

    window.requestAnimationFrame(() => {
      elements.modal.classList.add('wishlist-list-modal--open');
      if (mode === 'create') {
        elements.createInput?.focus({ preventScroll: true });
      } else {
        elements.panel?.focus({ preventScroll: true });
      }
    });
  };

  const saveWishlistListModalProduct = async () => {
    const elements = getWishlistListModalElements();
    const state = wishlistListModalState;

    if (!elements || !state?.product || !activeSwat) return;

    const selectedListIds = new Set(
      Array.from(elements.options.querySelectorAll('[data-wishlist-list-choice]:checked'))
        .map((input) => input.value)
        .filter(Boolean)
    );

    if (!selectedListIds.size && !state.initialListIds.size) {
      setWishlistListModalFeedback('Choose a list or create a new one.', true);
      return;
    }

    const listIdsToAdd = Array.from(selectedListIds).filter((listId) => !state.initialListIds.has(listId));
    const listIdsToRemove = Array.from(state.initialListIds).filter((listId) => !selectedListIds.has(listId));

    if (!listIdsToAdd.length && !listIdsToRemove.length) {
      closeWishlistListModal();
      return;
    }

    setWishlistListModalBusy(true);
    setWishlistListModalFeedback('Saving...');

    try {
      await Promise.all([
        ...listIdsToAdd.map((listId) => addWishlistItem(activeSwat, state.product, listId)),
        ...listIdsToRemove.map((listId) => removeWishlistItem(activeSwat, state.product, listId))
      ]);
      await refreshSurfaces({ renderPage: Boolean(document.querySelector('[data-wishlist-page]')), force: true });
      scheduleProductSocialCountRefresh({ force: true });
      closeWishlistListModal();
    } catch (error) {
      setWishlistListModalFeedback(error.message || 'Wishlist lists are unavailable right now.', true);
    } finally {
      setWishlistListModalBusy(false);
    }
  };

  const createWishlistListFromModal = async (event) => {
    event.preventDefault();

    const elements = getWishlistListModalElements();
    const state = wishlistListModalState;
    const name = elements?.createInput?.value?.trim() || '';

    if (!elements || !state || !activeSwat) return;

    if (name.length < 2) {
      setWishlistListModalFeedback('Enter a list name.', true);
      return;
    }

    setWishlistListModalBusy(true);
    setWishlistListModalFeedback('Creating list...');

    try {
      const list = await createWishlistList(activeSwat, name);
      const listId = list?.lid || '';

      if (state.mode === 'product' && state.product && listId) {
        await addWishlistItem(activeSwat, state.product, listId);
      }

      const elementsOnPage = getWishlistPageElements();

      if (state.mode === 'create' && listId) {
        setActiveWishlistListId(listId, elementsOnPage?.root);
      }

      await refreshSurfaces({ renderPage: Boolean(elementsOnPage), force: true });
      closeWishlistListModal();
    } catch (error) {
      setWishlistListModalFeedback(error.message || 'Wishlist list could not be created.', true);
    } finally {
      setWishlistListModalBusy(false);
    }
  };

  const initWishlistListModalEvents = () => {
    const elements = getWishlistListModalElements();
    if (!elements?.modal || elements.modal.dataset.wishlistListModalEvents === 'true') return;

    elements.modal.dataset.wishlistListModalEvents = 'true';
    elements.modal.addEventListener('click', (event) => {
      const closeButton = event.target instanceof Element ? event.target.closest('[data-wishlist-list-modal-close]') : null;
      const saveButton = event.target instanceof Element ? event.target.closest('[data-wishlist-list-save]') : null;

      if (closeButton) {
        event.preventDefault();
        closeWishlistListModal();
        return;
      }

      if (saveButton) {
        event.preventDefault();
        saveWishlistListModalProduct();
      }
    });

    elements.createForm?.addEventListener('submit', createWishlistListFromModal);

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || elements.modal.hidden) return;
      event.preventDefault();
      closeWishlistListModal();
    });
  };

  const renderWishlistCard = (product) => {
    const card = createElement('article', 'custom-wishlist-card');
    card.dataset.wishlistCard = '';
    card.dataset.wishlistProductId = product.productId;
    card.dataset.wishlistVariantId = product.variantId;
    card.dataset.wishlistListId = product.item.lid || '';
    card.dataset.wishlistListIds = product.listMemberships.map((membership) => membership.lid).filter(Boolean).join(',');
    card.dataset.wishlistProductUrl = product.removeProduct.du || product.url;
    card.dataset.wishlistTitle = product.title;

    const imageLink = createElement('a', 'custom-wishlist-card__image-link');
    imageLink.href = product.url;

    if (product.image) {
      const image = createElement('img', 'custom-wishlist-card__image');
      image.src = product.image;
      image.alt = product.title;
      image.loading = 'lazy';
      imageLink.append(image);
    } else {
      const placeholder = createElement('span', 'custom-wishlist-card__image-placeholder', product.title.slice(0, 2));
      imageLink.append(placeholder);
    }

    const body = createElement('div', 'custom-wishlist-card__body');
    const title = createElement('h2', 'custom-wishlist-card__title');
    const titleLink = createElement('a', '', product.title);
    titleLink.href = product.url;
    title.append(titleLink);

    body.append(title);

    if (product.variantTitle) {
      body.append(createElement('p', 'custom-wishlist-card__variant', product.variantTitle));
    }

    if (product.listMemberships.length > 1) {
      const listNames = product.listMemberships.map((membership) => membership.lname).filter(Boolean);

      if (listNames.length) {
        body.append(createElement('p', 'custom-wishlist-card__lists', `Saved in ${listNames.join(', ')}`));
      }
    }

    if (product.priceCents !== null && product.priceCents !== undefined) {
      const onSale = product.compareAtCents && product.compareAtCents > product.priceCents;
      const price = createElement('div', `custom-wishlist-card__price price${onSale ? ' price--on-sale' : ''}`);
      price.dataset.productPrice = '';
      price.dataset.productId = product.productId;
      price.dataset.variantId = product.variantId;
      price.dataset.basePriceCents = product.priceCents;
      price.dataset.baseCompareAtCents = onSale ? product.compareAtCents : 0;

      if (onSale) {
        const compareLabel = createElement('span', 'visually-hidden', 'Regular price');
        compareLabel.dataset.priceCompareLabel = '';
        const comparePrice = createElement('s', 'price__compare', formatMoney(product.compareAtCents));
        comparePrice.dataset.priceCompare = '';
        price.append(compareLabel, comparePrice);
      }

      const currentLabel = createElement('span', 'visually-hidden', onSale ? 'Sale price' : 'Regular price');
      currentLabel.dataset.priceCurrentLabel = '';
      const currentPrice = createElement('span', 'price__current', formatMoney(product.priceCents));
      currentPrice.dataset.priceCurrent = '';
      price.append(currentLabel, currentPrice);

      body.append(price);
    }

    const actions = createElement('div', 'custom-wishlist-card__actions');
    const addButton = createElement(
      product.available ? 'button' : 'a',
      product.available ? 'button button--full custom-wishlist-card__add' : 'button button--secondary button--full custom-wishlist-card__view',
      product.available ? 'Add to bag' : 'View product'
    );

    if (product.available) {
      addButton.type = 'button';
      addButton.dataset.wishlistAdd = product.variantId;
      addButton.setAttribute('aria-label', `Add ${product.title} to bag`);
    } else {
      addButton.href = product.url;
    }

    const removeButton = createElement('button', 'text-button custom-wishlist-card__remove', 'Remove');
    removeButton.type = 'button';
    removeButton.dataset.wishlistRemove = '';
    removeButton.setAttribute('aria-label', `Remove ${product.title} from wishlist`);

    actions.append(addButton, removeButton);
    body.append(actions);
    card.append(imageLink, body);

    return card;
  };

  const getWishlistPageElements = () => {
    const root = document.querySelector('[data-wishlist-page]');
    if (!root) return null;

    return {
      root,
      status: root.querySelector('[data-wishlist-status]'),
      toolbar: root.querySelector('[data-wishlist-toolbar]'),
      listControls: root.querySelector('[data-wishlist-list-controls]'),
      listDropdown: root.querySelector('[data-wishlist-list-dropdown]'),
      listValue: root.querySelector('[data-wishlist-list-value]'),
      listOptions: root.querySelector('[data-wishlist-list-options]'),
      createListButton: root.querySelector('[data-wishlist-create-list-open]'),
      countLabel: root.querySelector('[data-wishlist-count-label]'),
      grid: root.querySelector('[data-wishlist-grid]'),
      empty: root.querySelector('[data-wishlist-empty]')
    };
  };

  const setWishlistPageStatus = (elements, message = '', isError = false) => {
    if (!elements?.status) return;

    elements.status.hidden = !message;
    elements.status.textContent = message;
    elements.status.classList.toggle('custom-wishlist__status--error', isError);
  };

  const createWishlistListOption = ({ id, name, count, selected }) => {
    const option = createElement('button', 'custom-wishlist__list-option');
    const label = createElement('span', 'custom-wishlist__list-option-label', name);
    const meta = createElement('span', 'custom-wishlist__list-option-count', `${count} ${count === 1 ? 'item' : 'items'}`);

    option.type = 'button';
    option.dataset.wishlistListOption = '';
    option.dataset.wishlistListId = id;
    option.setAttribute('role', 'option');
    option.setAttribute('aria-selected', selected ? 'true' : 'false');
    option.append(label, meta);

    return option;
  };

  const renderWishlistListControls = (elements, lists, activeListId) => {
    if (!elements?.listControls || !elements.listOptions || !elements.listValue) return;

    const normalizedLists = normalizeWishlistLists(lists);
    const allCount = getWishlistItemsFromLists(normalizedLists, 'all').length;
    const options = [
      { id: 'all', name: 'All wishlists', count: allCount },
      ...normalizedLists.map((list) => ({
        id: list.lid,
        name: list.lname,
        count: list.listcontents.length
      }))
    ];
    const currentListId = activeListId || 'all';
    const currentOption = options.find((option) => option.id === currentListId) || options[0];

    elements.listControls.hidden = false;
    if (elements.createListButton) elements.createListButton.hidden = false;
    elements.listValue.textContent = currentOption?.name || 'All wishlists';
    elements.listOptions.replaceChildren(...options.map((option) => {
      return createWishlistListOption({
        ...option,
        selected: option.id === currentListId
      });
    }));
  };

  const getWishlistCountLabel = (count, lists, activeListId) => {
    const normalizedLists = normalizeWishlistLists(lists);
    const itemText = `${count} saved ${count === 1 ? 'item' : 'items'}`;

    if (activeListId && activeListId !== 'all') {
      const list = normalizedLists.find((candidate) => candidate.lid === activeListId);

      return list ? `${itemText} in ${list.lname}` : itemText;
    }

    if (!normalizedLists.length) return itemText;

    return `${itemText} across ${normalizedLists.length} ${normalizedLists.length === 1 ? 'list' : 'lists'}`;
  };

  const renderWishlistPage = async (swat, prefetched = null) => {
    const elements = getWishlistPageElements();
    if (!elements) return;

    elements.root.setAttribute('aria-busy', 'true');
    elements.grid.replaceChildren();
    elements.empty.hidden = true;
    setWishlistPageStatus(elements, 'Loading your wishlist...');

    try {
      const lists = prefetched?.lists || await fetchWishlistLists(swat);
      const totalItems = getWishlistItemsFromLists(lists, 'all');
      const activeListId = getActiveWishlistListId(lists, elements.root);
      const items = getWishlistItemsFromLists(lists, activeListId);

      setActiveWishlistListId(activeListId, elements.root);
      updateHeaderCount(totalItems.length);
      updateProductButtons(totalItems);
      renderWishlistListControls(elements, lists, activeListId);

      const products = await Promise.all(items.map(hydrateWishlistItem));
      elements.grid.replaceChildren(...products.map(renderWishlistCard));
      elements.toolbar.hidden = false;
      elements.empty.hidden = products.length > 0;
      elements.countLabel.textContent = getWishlistCountLabel(products.length, lists, activeListId);
      setWishlistPageStatus(elements);
    } catch (error) {
      setWishlistPageStatus(elements, 'Wishlist is not available right now. Please refresh and try again.', true);
    } finally {
      elements.root.setAttribute('aria-busy', 'false');
    }
  };

  const setCardButtonBusy = (button, label) => {
    button.disabled = true;
    button.classList.add('is-loading');
    button.dataset.originalText = button.textContent;
    button.textContent = label;
  };

  const resetCardButton = (button, label = '') => {
    button.disabled = false;
    button.classList.remove('is-loading');
    button.textContent = label || button.dataset.originalText || 'Add to bag';
  };

  const addCartItemWithToast = ({ id, quantity = 1, message = '' } = {}) => {
    return new Promise((resolve, reject) => {
      const detail = {
        id,
        quantity,
        message,
        handled: false,
        onSuccess: resolve,
        onError: reject
      };

      document.dispatchEvent(new CustomEvent('cart:add-item', { detail }));

      if (!detail.handled) {
        reject(new Error('Bag is not available right now.'));
      }
    });
  };

  const addWishlistCardToCart = async (button) => {
    const card = button.closest('[data-wishlist-card]');
    const variantId = getNumericOrStringId(card?.dataset.wishlistVariantId);

    if (!variantId) return;

    setCardButtonBusy(button, 'Adding...');

    try {
      await addCartItemWithToast({
        id: variantId,
        quantity: 1,
        message: card.dataset.wishlistTitle || ''
      });

      resetCardButton(button, 'Added');
      window.setTimeout(() => resetCardButton(button), 1600);
    } catch (error) {
      resetCardButton(button, 'Try again');
      button.setAttribute('aria-label', error.message);
    }
  };

  const removeWishlistCard = async (button) => {
    if (!activeSwat) return;

    const card = button.closest('[data-wishlist-card]');
    if (!card) return;

    const product = {
      epi: getNumericOrStringId(card.dataset.wishlistVariantId),
      empi: getNumericOrStringId(card.dataset.wishlistProductId),
      du: card.dataset.wishlistProductUrl
    };
    const listIds = (card.dataset.wishlistListIds || card.dataset.wishlistListId || '')
      .split(',')
      .map((listId) => listId.trim())
      .filter(Boolean);

    button.disabled = true;

    try {
      if (listIds.length) {
        await Promise.all(listIds.map((listId) => removeWishlistItem(activeSwat, product, listId)));
      } else {
        await removeWishlistItem(activeSwat, product, card.dataset.wishlistListId);
      }

      await refreshSurfaces({ renderPage: true, force: true });
    } catch (error) {
      button.disabled = false;
      button.textContent = 'Try again';
    }
  };

  const initWishlistPageEvents = () => {
    const elements = getWishlistPageElements();
    if (!elements || elements.root.dataset.wishlistEvents === 'true') return;

    elements.root.dataset.wishlistEvents = 'true';
    elements.root.addEventListener('click', (event) => {
      const createListButton = event.target.closest('[data-wishlist-create-list-open]');
      if (createListButton) {
        event.preventDefault();
        openWishlistListModal({ mode: 'create' });
        return;
      }

      const listOption = event.target.closest('[data-wishlist-list-option]');
      if (listOption) {
        event.preventDefault();
        setActiveWishlistListId(listOption.dataset.wishlistListId || 'all', elements.root);

        if (elements.listDropdown instanceof HTMLDetailsElement) {
          elements.listDropdown.open = false;
        }

        renderWishlistPage(activeSwat);
        return;
      }

      const addButton = event.target.closest('[data-wishlist-add]');
      if (addButton) {
        event.preventDefault();
        addWishlistCardToCart(addButton);
        return;
      }

      const removeButton = event.target.closest('[data-wishlist-remove]');
      if (removeButton) {
        event.preventDefault();
        removeWishlistCard(removeButton);
      }
    });
  };

  const bindSwymEvents = (swat) => {
    if (swymEventsBound || !swat?.evtLayer?.addEventListener) return;

    swymEventsBound = true;
    ['sw:addedtowishlist', 'sw:removedfromwishlist', 'sw:customerInfoRefreshed'].forEach((eventName) => {
      swat.evtLayer.addEventListener(eventName, () => {
        scheduleRefresh({ renderPage: Boolean(document.querySelector('[data-wishlist-page]')), force: true });
        scheduleProductSocialCountRefresh({ force: true });
      });
    });
  };

  const markWishlistUnavailable = () => {
    if (swymReady) return;

    document.querySelectorAll('[data-wishlist-button]').forEach((button) => {
      button.disabled = true;
      button.setAttribute('aria-label', 'Wishlist is unavailable');
    });

    const elements = getWishlistPageElements();
    if (elements) {
      setWishlistPageStatus(elements, 'Wishlist is not available right now. Please refresh and try again.', true);
    }
  };

  const initWithSwym = (swat) => {
    if (!swat) return;

    swymReady = true;
    activeSwat = swat;

    bindSwymEvents(swat);
    initVariantListener();
    initProductButtons(swat);
    initWishlistPageEvents();
    initWishlistListModalEvents();
    refreshSurfaces({ renderPage: Boolean(document.querySelector('[data-wishlist-page]')) });
    scheduleProductSocialCountRefresh();
  };

  window.SwymCallbacks = window.SwymCallbacks || [];

  const storedHeaderCount = readStoredHeaderCount();
  if (storedHeaderCount !== null) updateHeaderCount(storedHeaderCount, { persist: false });

  if (window._swat && typeof window._swat.fetch === 'function') {
    initWithSwym(window._swat);
  } else {
    window.SwymCallbacks.push(initWithSwym);
  }

  document.addEventListener('shopify:section:load', () => {
    if (!activeSwat) return;
    initProductButtons(activeSwat);
    initWishlistPageEvents();
    initWishlistListModalEvents();
    refreshSurfaces({ renderPage: Boolean(document.querySelector('[data-wishlist-page]')) });
    scheduleProductSocialCountRefresh();
  });

  window.setTimeout(markWishlistUnavailable, 8000);
})();
